/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ScormConfig } from '@coursera-web/widget-scorm';
import classNames from 'classnames';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';
import isMobileApp from 'js/lib/isMobileApp';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import { Button, Grid, Typography2, VisuallyHidden, breakpoints } from '@coursera/cds-core';
import { CloseIcon, MaximizeIcon } from '@coursera/cds-icons';

import Icon from 'bundles/iconfont/Icon';
import { refreshCourseViewGrade } from 'bundles/in-course-stores/actions/CourseViewGradeActions';
import { getProgress } from 'bundles/in-course-stores/actions/ProgressActions';
import ApplicationStore from 'bundles/ssr/stores/ApplicationStore';
import {
  connectToWidget,
  registerWidget,
  removeWidget,
  toggleWidgetError,
} from 'bundles/widget/actions/WidgetManagerActions';
import WidgetErrorMessage from 'bundles/widget/components/WidgetErrorMessage';
import { styles as headerStyles } from 'bundles/widget/components/cds/WidgetInPlaceModalHeader';
import ItemCompleteButton from 'bundles/widget/components/itemCompleteButton/ItemCompleteButton';
import { FocusableElements, RPCActions, WidgetReadyStates } from 'bundles/widget/constants/WidgetConstants';
import { useSetGradedWidgetGrade } from 'bundles/widget/hooks/useSetGradedWidgetGrade';
import type { ConnectedWidget } from 'bundles/widget/reducers/WidgetManagerReducer';
import { store as WidgetManagerStore } from 'bundles/widget/reducers/WidgetManagerReducer';
import type { CourseraConnectMessage, SetGradedCompleteRequest } from 'bundles/widget/types/Request';
import type { WidgetSession, WidgetSessionConfiguration, WidgetSessionId } from 'bundles/widget/types/WidgetSession';
import { isRpcAction } from 'bundles/widget/types/WidgetSession';
import { handleWidgetRpcAction } from 'bundles/widget/utils';

import _t from 'i18n!nls/widget';

import 'css!bundles/widget/components/__styles__/WidgetFrame';

const styles = {
  controlsRow: css`
    margin-top: var(--cds-spacing-400);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
    }
  `,
  markAsComplete: css`
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-300);
    }
  `,
  completedButtonOnly: css`
    ${breakpoints.down('xs')} {
      margin-top: 0;
    }
  `,
};

export type LegacyWidgetFrameProps = {
  onReceiveMessage?: (request: CourseraConnectMessage) => Promise<string | undefined | void>;
  session: WidgetSession;
  sessionId?: WidgetSessionId;
  courseId?: string;
  courseBranchId?: string;
  itemId?: string;
  inWidgetItem?: boolean;
  isDisabled?: boolean;
  // NOTE: isComplete and markComplete should only be true when inWidgetItem=true
  isComplete?: boolean;
  markComplete?: () => void;
  widgetId: string;
  parentRpcActions?: Array<string>;
  fallbackDefaultHeight?: string;
  showPopupButton?: boolean;
  widgetContentTitle?: string;
  onToggleExpanded?: () => void;
  nextItem?: ItemMetadata;
  isGradedPluginItem?: boolean;
};

type onReceiveMessageResponse = WidgetSessionConfiguration | string | undefined | void | any;

const KEY_TAB = 9;

export const LegacyWidgetFrame = (props: LegacyWidgetFrameProps) => {
  const {
    onReceiveMessage: onReceiveMessageFromProps = () => {
      return Promise.resolve();
    },
    session,
    sessionId,
    courseId,
    courseBranchId,
    itemId,
    inWidgetItem = false,
    isComplete,
    markComplete,
    widgetId,
    parentRpcActions = [],
    fallbackDefaultHeight = '500px',
    showPopupButton = true,
    widgetContentTitle,
    onToggleExpanded,
    nextItem,
    isGradedPluginItem,
  } = props;

  const executeAction = useFluxibleAction();
  const { setGrade: setGradedWidgetGrade } = useSetGradedWidgetGrade();

  const { widgetRegistry, widgetState, error } = useFluxibleStore(WidgetManagerStore, (store) => {
    const widgetManagerStoreState = store.getState();
    const widgetStateFromStore = widgetManagerStoreState.widgetStates[widgetId];
    const errorFromStore = widgetStateFromStore?.error;

    return {
      widgetRegistry: widgetManagerStoreState.widgetRegistry,
      widgetState: widgetStateFromStore,
      error: errorFromStore,
    };
  });

  const userAgent = useFluxibleStore(ApplicationStore, (store) => store.getUserAgent());

  // State hooks
  const [specifiedHeight, setSpecifiedHeight] = useState<string>('');
  const [expanded, setExpanded] = useState<boolean>(false);

  // Ref hooks
  const registeredWidgetRef = useRef<boolean | undefined>();
  const attemptedConnectionRef = useRef<boolean | undefined>();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const maximizeButtonRef = useRef<HTMLButtonElement | null>(null);
  const secretButtonRef = useRef<HTMLButtonElement | null>(null);
  const widgetFrameRef = useRef<HTMLDivElement | null>(null);
  const iframeElementRef = useRef<HTMLIFrameElement | null>(null);

  // Helper functions
  const isOnMobile = useCallback<() => boolean>(() => {
    const isMobileBrowser = userAgent?.isMobileBrowser;
    return isMobileApp.get() || isMobileBrowser;
  }, [userAgent]);

  const registerWidgetAction = useCallback<() => void>(() => {
    if (registeredWidgetRef.current) {
      return;
    }

    registeredWidgetRef.current = true;
    executeAction(registerWidget, {
      widgetId,
      widgetRegistry,
    });
  }, [executeAction, widgetId, widgetRegistry]);

  const setGradedComplete = useCallback<(request: SetGradedCompleteRequest) => Promise<string | undefined | void>>(
    async (request) => {
      if (!isGradedPluginItem) {
        markComplete?.();
        return onReceiveMessageFromProps?.(request);
      }
      const { body } = request;
      if (body && inWidgetItem && courseId && courseBranchId && itemId && sessionId) {
        const { rawScore, minScore, maxScore } = body;

        try {
          await setGradedWidgetGrade({
            rawScore,
            minScore,
            maxScore,
            widgetSessionId: sessionId,
            courseId,
            courseBranchId,
            itemId,
          });
          // the BE should have already updated the completion status so we just want to refetch the progress on the FE
          executeAction(refreshCourseViewGrade, { courseId });
          executeAction(getProgress, { courseId, refreshProgress: true });
          return onReceiveMessageFromProps?.(request);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : String(err);
          return Promise.reject(new Error(`Error setting the grade: ${errorMessage}`));
        }
      } else {
        return Promise.reject(new Error('Missing required parameters for graded completion'));
      }
    },
    [
      isGradedPluginItem,
      markComplete,
      onReceiveMessageFromProps,
      courseBranchId,
      courseId,
      executeAction,
      inWidgetItem,
      itemId,
      sessionId,
      setGradedWidgetGrade,
    ]
  );

  const onReceiveMessageCallback = useCallback<(request: CourseraConnectMessage) => Promise<onReceiveMessageResponse>>(
    async (request: CourseraConnectMessage): Promise<onReceiveMessageResponse> => {
      const { rpcActionTypes, configuration } = session;

      if (request.type === RPCActions.LOAD_WIDGET_ERROR) {
        const { body } = request;
        if (body) {
          const { error: errorFromRequest } = body;
          executeAction(toggleWidgetError, { widgetId, error: new Error(errorFromRequest) });
        }
        return Promise.resolve();
      }
      if (rpcActionTypes?.length) {
        if (isRpcAction(request.type) && rpcActionTypes.includes(request.type)) {
          if (request.type === RPCActions.GET_SESSION_CONFIGURATION) {
            return Promise.resolve(configuration);
          } else if (request.type === RPCActions.SET_COMPLETE) {
            markComplete?.();
            return onReceiveMessageFromProps?.(request);
          } else if (request.type === RPCActions.SET_GRADED_COMPLETE) {
            return setGradedComplete(request);
          } else if (request.type === RPCActions.SET_HEIGHT) {
            const { body } = request;
            if (body) {
              const heightValue = body.height;
              if (heightValue) {
                setSpecifiedHeight(heightValue);
              }
              return Promise.resolve('Height set');
            } else {
              return Promise.resolve('Failed to set height - empty request');
            }
          } else if (parentRpcActions && parentRpcActions.includes(request.type) && onReceiveMessageFromProps) {
            return onReceiveMessageFromProps(request);
          } else if (sessionId) {
            return handleWidgetRpcAction(request, sessionId);
          }
        } else {
          return Promise.reject(new Error(`Unknown or unauthorized request type: ${request.type}`));
        }
      }
      return Promise.resolve();
    },
    [
      session,
      executeAction,
      widgetId,
      markComplete,
      onReceiveMessageFromProps,
      parentRpcActions,
      sessionId,
      setGradedComplete,
    ]
  );

  const connectToWidgetAction = useCallback<() => void>(() => {
    if (attemptedConnectionRef.current) {
      return;
    }

    attemptedConnectionRef.current = true;
    const widgetRegistration = widgetRegistry[widgetId];
    const { rpcActionTypes } = session;

    if (widgetRegistration && iframeElementRef.current) {
      // Since DOM elements can't be passed through actionContext, send wrapper around `postMessage`.
      const sendMessageToIFrame = (message: $TSFixMe, src: $TSFixMe) => {
        if (iframeElementRef.current && iframeElementRef.current.contentWindow) {
          iframeElementRef.current.contentWindow.postMessage(message, src);
        }
      };

      executeAction(connectToWidget, {
        iFrameSrc: iframeElementRef.current.src || '',
        widgetId,
        rpcActionTypes: rpcActionTypes || [],
        onReceiveMessage: onReceiveMessageCallback as any,
        sendMessageToIFrame,
      });
    }
  }, [executeAction, widgetRegistry, widgetId, session, onReceiveMessageCallback]);

  const refreshWidgetState = useCallback<() => void>(() => {
    if (!widgetState || widgetState.readyState === WidgetReadyStates.NOT_LOADED) {
      registerWidgetAction();
    } else if (widgetId && widgetState.readyState === WidgetReadyStates.REGISTERING) {
      connectToWidgetAction();
    }
  }, [widgetState, widgetId, registerWidgetAction, connectToWidgetAction]);

  const onExpandToggle = useCallback<() => void>(() => {
    // TODO @sgogia
    // slightly hacky way of ensuring body (higher in hierarchy) can't be scrolled when widget expanded
    // document body checks are only required for getting rid of flow issues (body should be defined for this event)

    if (!expanded) {
      document.body.classList.add('noscroll');
    } else {
      document.body.classList.remove('noscroll');
    }

    if (onToggleExpanded) {
      onToggleExpanded();
    }

    const newExpandedState = !expanded;
    setExpanded(newExpandedState);

    // Handle focus after state update
    setTimeout(() => {
      if (newExpandedState && closeButtonRef.current) {
        closeButtonRef.current.focus();
      } else if (!newExpandedState && maximizeButtonRef.current) {
        maximizeButtonRef.current.focus();
      }
    }, 0);
  }, [expanded, onToggleExpanded]);

  const getFocusableElements = useCallback(() => {
    if (widgetFrameRef.current) {
      const allFocusableElements = [
        ...widgetFrameRef.current.querySelectorAll<HTMLElement>(FocusableElements.FOCUSABLE_SELECTOR),
      ];
      const focusableElementsWithoutDisabledButtons = allFocusableElements.filter((el) => !el.hasAttribute('disabled'));

      return focusableElementsWithoutDisabledButtons;
    }

    return [];
  }, []);

  const redirectTabFocus = useCallback<(e: KeyboardEvent, focusableElement: $TSFixMe) => void>(
    (e: KeyboardEvent, focusableElement: $TSFixMe) => {
      e.preventDefault();
      focusableElement.focus();
    },
    []
  );

  const showExpansionButton = useCallback<() => boolean>(() => {
    return !expanded && !isOnMobile() && showPopupButton;
  }, [expanded, isOnMobile, showPopupButton]);

  const showCompleteButton = useCallback<() => boolean>(() => {
    // If this is a SCORM plugin, we do not know ahead of time if the SCO will mark itself as complete so, although we
    // have the SET_COMPLETE RPC action, it might never be called. In this case, we let the configuration option set by the author
    // determine whether or not to show the completion button
    const isScorm = !!(session?.configuration as ScormConfig)?.SCORM_entry;
    const hideCompleteButtonForScorm = isScorm && (session?.configuration as ScormConfig)?.hideCompleteButton;
    const hasCompletedRPCAction =
      !isScorm &&
      session?.rpcActionTypes &&
      (session.rpcActionTypes.includes(RPCActions.SET_COMPLETE) ||
        session.rpcActionTypes.includes(RPCActions.SET_GRADED_COMPLETE));

    const showCompletionButton = !expanded && inWidgetItem && !hasCompletedRPCAction && !hideCompleteButtonForScorm;
    return showCompletionButton;
  }, [expanded, inWidgetItem, session]);

  const cycleTabs = useCallback<(event: KeyboardEvent) => void>(
    (event: KeyboardEvent) => {
      if (!expanded || event.keyCode !== KEY_TAB) {
        return;
      }

      // grab it every time in case the modal is updated and there's new contents in there
      const focusableElements = getFocusableElements();

      const lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLButtonElement;
      const firstFocusableElement = focusableElements[0] as HTMLButtonElement;
      const currentElement = document.activeElement;

      const showExpansionButtonResult = showExpansionButton();
      const showCompletionButtonResult = showCompleteButton();

      if (event.shiftKey) {
        // SHIFT + TAB
        if (currentElement === firstFocusableElement) {
          const isCompletionButtonHidden = !showExpansionButtonResult && !showCompletionButtonResult;
          /* tab to iframe if completion button is hidden */
          redirectTabFocus(event, isCompletionButtonHidden ? iframeElementRef.current : lastFocusableElement);
        }
      } else if (currentElement === lastFocusableElement) {
        // TAB
        redirectTabFocus(event, firstFocusableElement);
      }
    },
    [expanded, getFocusableElements, showExpansionButton, showCompleteButton, redirectTabFocus]
  );

  const tabToCloseButton = useCallback<() => void>(() => {
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, []);

  const trySmartHeightDefault = useCallback<() => void>(() => {
    const iframe = iframeElementRef.current;
    if (!!iframe && !specifiedHeight) {
      try {
        const contentsHeight = iframe?.contentWindow?.document.body.scrollHeight;
        if (contentsHeight && contentsHeight > 0) {
          iframe.height = contentsHeight + 'px';
        }
      } catch {
        // if we can't get the height of the contents, we'll keep our default height
      }
    }
  }, [specifiedHeight]);

  const renderSpinner = useCallback<(width: string, height: string | undefined) => JSX.Element>(
    (width: string, height: string | undefined) => {
      return (
        <div
          className="horizontal-box align-items-absolute-center widget-loader"
          style={{ width, height }}
          data-testid="widget-spinner"
        >
          <Icon name="spinner" size="2x" spin={true} className="color-hint-text" />
        </div>
      );
    },
    []
  );

  // useEffect hooks for lifecycle methods
  useEffect(() => {
    if (iframeElementRef.current && iframeElementRef.current instanceof HTMLIFrameElement) {
      // set as custom attribute since react 15 automatically removes the allow attributes in iframes
      // https://github.com/facebook/react/issues/12225
      iframeElementRef.current.setAttribute('allow', 'camera');
      iframeElementRef.current.focus();
    }

    document.addEventListener('keydown', cycleTabs, false);

    return () => {
      // componentWillUnmount cleanup
      document.removeEventListener('keydown', cycleTabs, false);
    };
  }, [cycleTabs]);

  // Separate useEffect for widget cleanup on unmount
  useEffect(() => {
    return () => {
      if (!widgetState) {
        return;
      }

      const widgetRegistration = widgetRegistry[widgetId];

      if (widgetId && widgetRegistration && (widgetRegistration as ConnectedWidget)?.removeListenerHandle) {
        executeAction(removeWidget, {
          widgetId,
          removeListenerHandle: (widgetRegistration as ConnectedWidget)?.removeListenerHandle,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array for cleanup only on unmount

  // Initial setup and updates
  useEffect(() => {
    // componentDidMount and componentDidUpdate equivalent
    refreshWidgetState();
  }, [refreshWidgetState]);

  // Render logic
  const { src, sandbox } = session;
  const height = specifiedHeight || fallbackDefaultHeight;
  const width = '100%';

  if (!widgetState) {
    return renderSpinner(width, height);
  }

  const { readyState } = widgetState;
  const isWidgetLoading = readyState === WidgetReadyStates.REGISTERING || readyState === WidgetReadyStates.NOT_LOADED;

  // Because reference to iFrame is needed, hide iFrame via CSS instead of not rendering it.
  const iframeClassName = classNames('widget-iframe', {
    hidden: !!error,
    'default-height': !specifiedHeight,
  });

  // TODO: separate renderExpandedView into its own function
  const expandedClass = expanded ? 'expanded' : 'unexpanded';
  const showExpansionButtonResult = showExpansionButton();
  const showCompletionButtonResult = showCompleteButton();

  // if the widget frame is expanded we should give it modal semantics
  const dialogProperties = expanded ? { 'aria-modal': true, role: 'dialog' } : {};

  return (
    <div ref={widgetFrameRef} className={`rc-WidgetFrame ${expandedClass} legacyWidgetFrame`} {...dialogProperties}>
      {expanded && <div className="dimmer" />}
      <div className="widget-container vertical-box">
        {expanded && (
          <Grid
            data-testid="widget-modal"
            css={headerStyles.modalHeader}
            justifyContent={widgetContentTitle ? 'space-between' : 'flex-end'}
            container
          >
            <Typography2 component="h1" variant="titleSmall" css={headerStyles.truncated}>
              {widgetContentTitle}
            </Typography2>

            <Button
              type="button"
              aria-label={_t('#{widgetContentTitle} window. Close window', { widgetContentTitle })}
              onClick={onExpandToggle}
              icon={<CloseIcon size="small" />}
              iconPosition="after"
              variant="ghost"
              size="small"
              className="read-only-wrapper-allow-interaction"
              ref={closeButtonRef}
            >
              {_t('Close')}
            </Button>
          </Grid>
        )}
        <div className="frame-container">
          {error && <WidgetErrorMessage />}
          {isWidgetLoading && renderSpinner(width, height)}

          <iframe
            title={session.iframeTitle}
            src={src}
            id={widgetId}
            height={height}
            className={iframeClassName}
            width={width}
            sandbox={sandbox}
            ref={iframeElementRef}
            onLoad={trySmartHeightDefault}
            data-testid="widget-iframe"
          >
            {_t('Iframes are not supported in your browser.')}
          </iframe>
        </div>

        {showExpansionButtonResult && (
          <div className="widget-controls" css={styles.controlsRow}>
            <Button
              ref={maximizeButtonRef}
              onClick={onExpandToggle}
              aria-label={_t('Expand #{widgetContentTitle} window', { widgetContentTitle })}
              variant="secondary"
              iconPosition="before"
              size="medium"
              icon={<MaximizeIcon />}
            >
              {_t('Expand')}
            </Button>
            {showCompletionButtonResult && typeof isComplete === 'boolean' && !!markComplete && (
              <div css={isComplete ? {} : styles.markAsComplete}>
                <ItemCompleteButton isComplete={isComplete} markComplete={markComplete} nextItem={nextItem} />
              </div>
            )}
          </div>
        )}
        {/* Helps us tab back to the close button if the complete button does not exist.
          This is because an iframe is used to display contents */}
        {!showExpansionButtonResult && !showCompletionButtonResult && (
          <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
            <button
              aria-hidden={expanded}
              type="button"
              className="secret-button"
              ref={secretButtonRef}
              onFocus={tabToCloseButton}
            >
              {_t('Go to Close')}
            </button>
          </VisuallyHidden>
        )}
        {!showExpansionButtonResult &&
          showCompletionButtonResult &&
          typeof isComplete === 'boolean' &&
          !!markComplete && (
            <div
              className="widget-controls"
              aria-hidden={expanded}
              css={[styles.controlsRow, isComplete ? styles.completedButtonOnly : {}]}
            >
              <ItemCompleteButton isComplete={isComplete} markComplete={markComplete} />
            </div>
          )}
      </div>
    </div>
  );
};

export default LegacyWidgetFrame;
